import {useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {Box, Grid, SxProps, Typography} from '@mui/material';

import {ColumnDefinition, CulturalDashboardTable} from '../shared/CulturalDashboardTable';

import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {AllStrengths, Strengths} from '../../types/CompanyStrengthsTypes';

interface CompanyStrengthsProps {
  companyStrengths: Strengths;
}

export default function CompanyStrengths({companyStrengths}: CompanyStrengthsProps) {
  const {t} = useTranslation();

  const currentLanguage = useContext(LanguageContext);

  const [open, setOpen] = useState<boolean>(false);
  const [strengths, setStrengths] = useState<AllStrengths[]>();

  const linkStyles: SxProps = {
    marginTop: 1,
    color: greenColors[700],
    verticalAlign: 'middle',
    cursor: 'pointer',
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    const orderedStrengths = companyStrengths.all.sort((a, b) => b.percentage - a.percentage);
    setStrengths(orderedStrengths);
  }, [companyStrengths]);

  const renderTopThreeStrengths = (topStrengths: string[]) => {
    if (!Array.isArray(topStrengths) || topStrengths.length === 0) {
      return null;
    }

    return topStrengths.map((topStrength, index) => (
      <Box
        key={index}
        sx={{
          backgroundColor: 'white',
          padding: '32px 16px',
          borderRadius: 2,
          border: `1px solid ${orangeColors[3]}`,
          flexBasis: {xs: '100%', md: '30%'},
          flexGrow: 1,
        }}
      >
        <Typography variant='body1' textAlign={'center'}>
          {topStrength}
        </Typography>
      </Box>
    ));
  };

  const strengthsColumns: ColumnDefinition<AllStrengths>[] = useMemo(() => {
    return [
      {
        key: 'label',
        label: t('companyCultureDashboard.strengths.tableHeadTile'),
        render: (item) => item.label[currentLanguage],
      },
      {
        key: 'distribution',
        label: '#',
        render: (item) => item.distribution,
      },
      {
        key: 'percentage',
        label: '%',
        render: (item) => `${Math.round(item.percentage * 100)}%`,
      },
    ];
  }, [currentLanguage, t]);

  return (
    <>
      <Grid
        container
        sx={{
          marginTop: 8,
          marginBottom: 2,
        }}
      >
        <Grid container item sx={{display: 'flex', alignItems: 'center', mb: 2}}>
          <Grid item sx={{mr: {xs: '0px', sm: '16px'}}}>
            <Typography variant='h2'>{t('companyCultureDashboard.strengths.title')}</Typography>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={handleOpen}
          >
            <Typography variant='body1' className='hover-target' sx={{...linkStyles}}>
              {t('companyCultureDashboard.strengths.subTitle')}
            </Typography>
            <ArrowForwardIosIcon
              className='hover-target'
              sx={{fontSize: '1rem', position: 'relative', ml: 1, ...linkStyles}}
            />
          </Box>
          <EmpionDialog
            open={open}
            onClose={handleClose}
            sx={{'& .MuiDialogContent-root': {padding: '32px'}}}
            backgroundColor='white'
          >
            <CulturalDashboardTable
              onClose={handleClose}
              data={strengths || []}
              columns={strengthsColumns}
              isCulturalDashboard
              alternativeTitle={t('companyCultureDashboard.strengths.subTitle') as string}
            />
          </EmpionDialog>
        </Grid>
        <Grid container item gap={2}>
          {renderTopThreeStrengths(companyStrengths.topThree[currentLanguage])}
        </Grid>
      </Grid>
    </>
  );
}
